import { Text } from "@mantine/core";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "src/components/shared/BackButton/BackButton";
import TileGrid from "src/components/shared/NavigationTile/TileGrid";
import { type IGroupedLog, type ILog } from "src/interfaces/ChecklistLogbooks";
import { Flex } from "src/styled";

import { groupLogbooks } from "../CheckList.utils";
import styles from "../checklistRow.module.scss";
import { useChecklistsContext } from "../CheckListsContext";
import ChecklistLogbookCard from "./ChecklisLogbookCard";

const ChecklistLogbooks = () => {
  const navigate = useNavigate();
  const { collectionId } = useParams();
  const { tileLogbooks, setResetTileSearch } = useChecklistsContext();
  const groupedLogs = useMemo(
    () => groupLogbooks(tileLogbooks as ILog[]),
    [tileLogbooks],
  );

  useEffect(() => {
    setResetTileSearch?.(Math.random());
  }, [collectionId, setResetTileSearch]);

  const selectedLogbook = useMemo(() => {
    if (!collectionId) return null;
    return groupedLogs.find(l => l.value === collectionId) ?? null;
  }, [collectionId, groupedLogs]);

  const handleSelectLogbook = useCallback(
    (name: string) => {
      const listToLookIn = selectedLogbook?.subSections ?? groupedLogs;
      const logbook = listToLookIn.find(l => l.label === name) as IGroupedLog;
      if (!logbook) return;

      if (logbook.subSections && logbook.subSections.length > 0) {
        navigate(`./collection/${logbook.value}`);
        return;
      }

      if (collectionId) {
        navigate(`../logbook/${collectionId}/${logbook.value}`);
        return;
      }

      navigate(`./logbook/${logbook.value}`);
    },
    [collectionId, groupedLogs, navigate, selectedLogbook],
  );

  const renderLogbooks = useCallback(() => {
    const tiles =
      selectedLogbook === null
        ? groupedLogs
        : selectedLogbook.subSections;

    return tiles
      .filter(l => l.label)
      .map((l, i) => (
        <ChecklistLogbookCard
          key={`checklist-lb-${l.value || l.label}-${i}`}
          {...l}
          onClick={handleSelectLogbook}
        />
      ));
  }, [groupedLogs, handleSelectLogbook, selectedLogbook]);

  return (
    <Flex direction="column">
      {selectedLogbook && selectedLogbook?.subSections.length > 0 ? (
        <Flex align="center" className={styles.sectionHeader}>
          <BackButton onClick={() => navigate("..")} />
          <Text className={styles.sectionTitle}>{selectedLogbook.label}</Text>
        </Flex>
      ) : (
        <Flex align="center" className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>All Logbooks</Text>
        </Flex>
      )}
      {(selectedLogbook === null || selectedLogbook.subSections.length > 0) && (
        <TileGrid variant="compact">{renderLogbooks()}</TileGrid>
      )}
    </Flex>
  );
};

export default ChecklistLogbooks;
